/**
 * @file bulkBillPlan.js
 * @version 1.8.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-28
 * @description Bulk Add Bills (Cam, 2026-10-12): what Add Bill would make of
 *              one bill read from a batch, worked out without the screen —
 *              so the summary can say whether it is Ready to accept, what an
 *              accept would change, and why one needs review — and the save
 *              Accept sends.
 *
 *              It is Add Bill's upload path, step for step, through the same
 *              utils: the supplier the invoice names (utils/billSupplierDefault,
 *              billSupplierMatch), the date moved off a locked period
 *              (billLockDate), the invoice's lines reconciled with the quoted
 *              PO's (billLineReconcile.autoMatch / reconcileState, the PO's
 *              lines from /generate-from-pos), every line on an account, the
 *              lines against the invoice's net total, the totals against the
 *              invoice's, the quantity check against supplied / ordered
 *              (billQtyCheck), and the PO costs it would change
 *              (billCostWriteBack.poCostChanges — Add Bill's default: every
 *              line ticked, as the bill is not Queried). Supplied quantities
 *              are never copied (that is always the user's tick). A credit,
 *              an unreadable page, a possible duplicate and anything Add Bill
 *              would stop a save over all need review.
 *
 *              Accept is the normal save (POST /api/bills) with the batch
 *              item's id: Approved, sent to Xero as normal.
 *
 * 1.1.0 (2026-10-12, review — "Update PO from bill is the USER'S CHOICE"):
 *              the plan's save carries no PO cost choice; Accept asks, line
 *              by line (every line ticked, as Add Bill does), and
 *              acceptPayload(plan, ticks) sends exactly what was ticked.
 * 1.2.0 (2026-10-13, Jade — "Bill & PO Units Differ"): a bill line priced
 *              per tonne against a PO line in packs (or the other way round)
 *              is checked and costed in the PO line's unit
 *              (utils/billUnitConversion), as Add Bill does: no query over
 *              1.00 T against 40 × 25kg bag, and the cost offered is the bag's.
 * 1.3.0 (2026-10-14, Cam — one bill line over several PO lines): a sure
 *              split is made as Add Bill makes it (utils/billLineSplit.autoSplit
 *              — the line's quantity exactly the PO lines' added up, one set
 *              only), so Accept saves one line per PO line. A split that is
 *              only offered (a quantity a little different, a choice of PO
 *              lines) leaves the bill for review, saying so.
 * 1.4.0 (2026-10-14, Cam): a bill quoting a PO already billed for its goods
 *              (utils/billPoReference) is never accepted from the summary:
 *              it needs review (the reason says so), where Add Bill asks.
 * 1.4.1 (2026-10-14, Cam — Lacto Production): the quantity check takes the
 *              invoice's unit as Add Bill does (a bill per kg against bags).
 * 1.5.0 (2026-10-14, Cam — Burcombe's QA03476): a bill whose jobs quote
 *              their own orders (`reference_links` from the reader): each
 *              exact PO-H suggestion not already billed is linked before
 *              Accept (utils/billReferenceLinks.bulkApply); any other job —
 *              likely, possible, no match, a PO-D, a haulage link, already
 *              billed — sends the bill to review with the reason.
 * 1.6.0 (2026-10-16, Cam — "each sub line … an inventory item of ours"):
 *              each charge line of a job takes its EXTH item (EXTH FS for a
 *              fuel surcharge; Bag or Bulk from the linked PO-H), the item's
 *              account and the document's reference in its words, as Add
 *              Bill does (utils/billJobLines.chargeItemsOnLines).
 * 1.8.0 (2026-10-28, Cam — "make the cost field mandatory and not chosen by
 *              default" where the bill's cost differs from the order's or the
 *              contract's): such a bill is never Ready — it goes to review,
 *              where Add Bill asks for the cost (utils/billCostCheck, the
 *              billing context's `cost_check`).
 * 1.7.0 (2026-10-16): only on a haulier's bill of jobs
 *              (billJobLines.isBillOfJobs) — a goods bill quoting our POs
 *              (MVFS, Alta) keeps its lines as read.
 */

import { billLinesFromApi, billLinesToPayload } from './lineItems';
import { lineTotals, lineIsEmpty } from '../components/OrderLinesGrid';
import { supplierFromUpload } from './billSupplierDefault';
import { supplierClash, invoiceSupplierText, unmatchedSupplierNote } from './billSupplierMatch';
import { openBillDate, lockedDateNotice } from './billLockDate';
import { autoMatch, reconcileState, poLineKey } from './billLineReconcile';
import { autoSplit, splitOffers, poLineFacts, offerText } from './billLineSplit';
import { billLineQuantityCheck, compareQuantity, queryQuantityText } from './billQtyCheck';
import { poCostChanges, defaultCostTicks, poCostUpdatesPayload, billCostsByKey, costKey, suppliedFromContext } from './billCostWriteBack';
import { conversionText, lineTotalsCheck, lineTotalsText } from './billUnitConversion';
import { findItemForLine } from './lineItems';
import { formatCurrency } from './currencyFormatter';
import { poQuantityFor } from './billPoEdits';
import { billEditMode } from './billEdit';
import { billedPoReviewReason } from './billPoReference';
import { bulkApply } from './billReferenceLinks';
import { chargeItemsOnLines, isBillOfJobs } from './billJobLines';
import { costCheck, billCostInPoUnit, costCheckText } from './billCostCheck';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Row statuses, in the summary's words. */
export const PLAN_STATUS = Object.freeze({
  reading: 'Reading…',
  ready: 'Ready',
  review: 'Needs review',
  duplicate: 'Possible duplicate',
  unreadable: 'Unreadable',
  not_a_bill: 'Not a bill',
  failed: 'Could not be read',
  checking: 'Checking…',
  saved: 'Saved',
  replaced: 'Replaced existing',
  kept_existing: 'Kept existing',
  skipped: 'Skipped',
  discarded: 'Discarded',
});

const DONE = ['saved', 'replaced', 'kept_existing', 'skipped', 'discarded'];

/** Is the row a bill or credit read and still to settle? */
export const isOpenBill = (item) => !item?.outcome && item?.read_state === 'read' && (item.kind === 'bill' || item.kind === 'credit');

/** The bill's PO, if the invoice quotes one we hold. */
export const matchedPoId = (item) => Number(item?.parsed?.suggested?.matched_po?.id) || null;

/**
 * Add Bill's lines and checks for one read bill.
 *
 * @param {object} item — a batch item from GET /api/bulk-bills/batches/:id
 * @param {object} ctx
 * @param {Array<object>} ctx.products — inventory items (Add Bill's list)
 * @param {Object<string, object>} ctx.generatedByPo — { [poId]: /generate-from-pos bill } (undefined: still loading; null: failed)
 * @param {Object<string, object>} ctx.poContext — POST /api/purchase-orders/billing-context `pos`
 * @param {string|null} ctx.firstAllowedDate — useXeroLock
 * @param {string} ctx.today — YYYY-MM-DD
 * @param {boolean} ctx.seeCosts — costs.view
 * @param {object|null} ctx.batchOriginal — the earlier item this one duplicates in the batch
 * @returns {object} the plan
 */
export function planBulkBill(item, ctx = {}) {
  const plan = { status: 'review', reasons: [], notes: [], effects: [], payload: null, costChanges: [], kind: item?.kind };
  if (!item) return plan;
  if (DONE.includes(item.outcome)) return { ...plan, status: item.outcome };
  if (item.read_state === 'pending' || item.read_state === 'reading') return { ...plan, status: 'reading' };
  if (item.kind === 'unreadable') {
    return { ...plan, status: 'unreadable', reasons: [item.read_error || 'Nothing on these pages could be read — type the bill in by hand, or discard the pages.'] };
  }
  if (item.kind === 'other') {
    return { ...plan, status: 'not_a_bill', reasons: ['These pages do not look like a bill (a statement, delivery note or cover sheet?) — review them by hand, or discard them.'] };
  }
  if (item.read_state === 'failed') {
    return { ...plan, status: 'failed', reasons: [item.read_error || 'It could not be read — review it by hand, or discard it.'] };
  }

  const s = item.parsed?.suggested || {};
  const reasons = [];
  const notes = [];
  const credit = item.kind === 'credit';
  if (credit) reasons.push('A supplier credit note — reviewed in Add Supplier Credit (say what it is for), never saved as a bill.');

  // ── Supplier (Add Bill: the one the invoice names; else the PO's, to check) ──
  const supplier = supplierFromUpload(s.supplier_match, s.matched_po);
  if (!supplier || !supplier.accountId) {
    reasons.push(unmatchedSupplierNote(s.supplier_match) || 'The supplier could not be found — choose it.');
  } else if (supplier.needsCheck) {
    reasons.push(`${supplier.hint}.`);
  }
  const clash = s.matched_po
    ? supplierClash(s.supplier_match, { accountId: s.matched_po.account_id, name: s.matched_po.supplier_name })
    : null;
  if (clash) {
    reasons.push(`The invoice is from ${invoiceSupplierText(clash)}, but ${s.matched_po.purchase_order_number} is ${clash.poName || "another supplier"}'s PO.`);
  }

  // ── Header ──
  const supplierReference = String(s.supplier_reference || '').trim();
  if (!supplierReference) reasons.push(`No ${credit ? 'credit note' : 'invoice'} number was read — Xero files it under that number.`);
  let billDate = /^\d{4}-\d{2}-\d{2}/.test(String(s.bill_date || '')) ? String(s.bill_date).slice(0, 10) : null;
  if (!billDate) {
    reasons.push('No invoice date was read.');
    billDate = ctx.today || null;
  }
  const moved = billDate ? openBillDate(billDate, ctx.firstAllowedDate) : { date: billDate, moved: false };
  if (moved.moved) notes.push(lockedDateNotice({ from: billDate, to: moved.date, source: 'supplier' }));
  const dueDate = /^\d{4}-\d{2}-\d{2}/.test(String(s.due_date || '')) ? String(s.due_date).slice(0, 10) : null;

  // ── Lines: the invoice's, reconciled with the quoted PO's ──
  const products = ctx.products || [];
  let pdfLines = billLinesFromApi(s.line_items || [], products, { fromPdf: true }).map(l => ({ ...l, _fromPdf: !credit }));
  if (!pdfLines.length) reasons.push('No lines were read from it.');
  // The PO each job quotes (2026-10-14): exact PO-H links made here, the rest
  // reviewed (utils/billReferenceLinks.bulkApply).
  let refPoIds = [];
  let refPoMeta = [];
  if (!credit && s.reference_links?.jobs?.length) {
    const jobs = s.reference_links.jobs;
    const ids = pdfLines.map(l => l.id);
    const r = bulkApply(pdfLines, jobs, ids);
    // Each charge line an EXTH item, the reference in its words, as Add Bill
    // does (utils/billJobLines, 2026-10-16) — for the links made here.
    const made = Object.fromEntries(jobs.map(j => [j.job_key, { po_id: j.target && r.poIds.includes(j.target.po_id) ? j.target.po_id : null }]));
    // Only a haulier's bill of jobs (utils/billJobLines.isBillOfJobs) — a
    // goods bill quoting our POs keeps its lines as read (2026-10-16).
    pdfLines = isBillOfJobs(s.line_items, jobs) ? chargeItemsOnLines(r.lines, jobs, made, ids, { items: products }) : r.lines;
    refPoIds = r.poIds;
    refPoMeta = r.poMeta;
    reasons.push(...r.reasons);
  }
  // The PO it quotes is already billed for its goods (Cam, 2026-10-14):
  // never linked again from the summary — reviewed in Add Bill, which asks
  // (take the link off, or keep the number as the PO Reference, as Stock).
  const billedReason = credit ? null : billedPoReviewReason(s.matched_po);
  if (billedReason) reasons.push(billedReason);
  const poId = credit || billedReason ? null : matchedPoId(item);
  let gen = null;
  if (poId) {
    gen = ctx.generatedByPo ? ctx.generatedByPo[poId] : undefined;
    if (gen === undefined) return { ...plan, status: 'checking', reasons: [`Loading ${s.matched_po.purchase_order_number}…`] };
    if (gen === null) reasons.push(`${s.matched_po.purchase_order_number}'s lines could not be loaded — review it.`);
  }
  const poLines = gen ? billLinesFromApi(gen.line_items, products).map((l, i) => ({
    ...l,
    _fromPo: true,
    _poHaulageLine: gen.line_items?.[i]?.kind === 'haulage' && !gen.line_items?.[i]?.po_line_key,
    // A Direct Delivery PO's haulage line: never matched to a goods line (billLineReconcile).
    ...(gen.line_items?.[i]?.kind === 'haulage' && gen.line_items?.[i]?.po_line_key ? { _poKind: 'haulage' } : {}),
  })) : [];
  const genPoIds = gen ? (gen.purchase_order_ids || []).map(Number) : [];
  const linkedPoIds = [...genPoIds, ...refPoIds.map(Number).filter(id => !genPoIds.includes(id))];
  const poById = {};
  for (const p of refPoMeta) poById[p.id] = p;
  for (const p of gen?.linked_po_meta || []) poById[p.id] = p;
  // Sure matches, then sure splits of one invoice line over several PO lines
  // (utils/billLineSplit) — exactly as Add Bill makes them.
  const unitWeightOf = (code) => findItemForLine(products, { code })?.unit_weight_kg ?? null;
  const factsOf = (id, key) => poLineFacts({ po: poById[id], key, ctx: ctx.poContext?.[id] || null, itemWeightOf: unitWeightOf });
  const itemOf = (l) => products.find(p => p.id === l?.inventory_item_id) || null;
  const matched = autoMatch([...pdfLines, ...poLines]).lines;
  const lines = credit ? matched : autoSplit(matched, { factsOf, itemOf }).lines;
  for (const offer of credit ? [] : splitOffers(lines, { factsOf, itemOf })) {
    const source = lines.find(l => l.id === offer.sourceId);
    if (source) reasons.push(`${offerText(offer, source)} Split it in review.`);
  }
  const splitCount = new Set(lines.filter(l => l._split).map(l => l._split.group)).size;
  if (splitCount) notes.push(`${plural(splitCount, 'invoice line')} split across PO lines — one line per PO line, adding up to the invoice's.`);
  const rec = reconcileState(lines, { active: !credit && pdfLines.length > 0 && linkedPoIds.length > 0, isEmpty: lineIsEmpty });
  if (rec.notOnPo.length) reasons.push(`${plural(rec.notOnPo.length, 'invoice line')} not on a line of the PO.`);
  if (rec.notOnInvoice.length) reasons.push(`${plural(rec.notOnInvoice.length, 'PO line')} not on the invoice.`);

  const real = lines.filter(l => !lineIsEmpty(l));
  const noAccount = real.filter(l => !l.inventory_item_id && !l.account_code).length;
  if (noAccount) reasons.push(`${plural(noAccount, 'line')} with no item or account — choose one.`);
  const unmatchedItems = (s.line_items || []).filter(l => l.unmatched).length;
  if (unmatchedItems && !noAccount) notes.push(`${plural(unmatchedItems, 'line')} matched no inventory item.`);

  const payloadLines = billLinesToPayload(real, lineTotals);
  const subTotal = r2(payloadLines.reduce((a, l) => a + (l.line_amount || 0), 0));
  const totalTax = r2(payloadLines.reduce((a, l) => a + (l.tax_amount || 0), 0));
  const total = r2(subTotal + totalTax);

  // ── Against the invoice's own figures ──
  const invoiceNet = num(s.sub_total);
  if (invoiceNet === null && real.length) {
    reasons.push('The invoice\'s total before VAT was not read — check the lines against it.');
  } else if (invoiceNet !== null && Math.abs(invoiceNet - subTotal) >= 0.005) {
    reasons.push(`The lines come to ${formatCurrency(subTotal)} and the invoice to ${formatCurrency(invoiceNet)} before VAT.`);
  }
  const invoiceTotal = num(s.total);
  if (invoiceTotal !== null && Math.abs(invoiceTotal - total) >= 0.01) {
    reasons.push(`The total is ${formatCurrency(total)}, the invoice says ${formatCurrency(invoiceTotal)}.`);
  }
  if (linkedPoIds.length && real.some(l => l.purchase_order_id)) {
    const without = linkedPoIds.filter(id => !real.some(l => Number(l.purchase_order_id) === id));
    if (without.length) reasons.push(`${without.map(id => poById[id]?.purchase_order_number || `PO #${id}`).join(', ')} has no line on the bill.`);
    const noPo = real.filter(l => !l.purchase_order_id && !l._refNoPo).length;
    if (noPo > 1) reasons.push(`${noPo} lines are on no PO (one may be).`);
  }

  // ── Quantities against what was supplied (else ordered), in the PO line's
  // unit — a bill per tonne against bags converted (utils/billUnitConversion) ──
  const groups = billCostsByKey(payloadLines.filter(l => l.purchase_order_id && l.po_line_key));
  for (const l of real) {
    const po = poById[l.purchase_order_id];
    if (!po || po.po_type === 'haulage' || !l.po_line_key) continue;
    const items = Array.isArray(po.line_items) ? po.line_items : [];
    const index = items.findIndex((li, i) => poLineKey(li, i) === l.po_line_key);
    if (index < 0 || items[index].kind === 'haulage') continue;
    const li = items[index];
    const pctx = ctx.poContext?.[po.id];
    const item = products.find(p => p.id === l.inventory_item_id) || null;
    const ordered = poQuantityFor(li, null, pctx?.scope || null);
    const { qty: poQty, basis } = compareQuantity({ ordered, supplied: pctx?.lines?.[l.po_line_key]?.supplied, xeroMade: !!pctx?.noSupplied });
    const g = groups.get(costKey(po.id, l.po_line_key));
    const check = billLineQuantityCheck({
      item, line: l, poLine: li, itemWeight: unitWeightOf(li.itemCode),
      billQty: g ? g.qty : num(l.quantity), billCost: g ? g.cost : num(l.unit_price), billAmount: g ? g.amount : null,
      billUnit: g ? (g.unit ?? null) : (l.bill_unit ?? null),
      poCost: li.unitAmount, poQty, ordered,
    });
    const { kind, billQty, conversion } = check;
    if (conversion) {
      const totals = lineTotalsCheck({ billAmount: g ? g.amount : null, poQty, poCost: li.unitAmount });
      notes.push(`${l.product_name || li.description || 'A line'}: ${conversionText(conversion)} ${lineTotalsText(totals, basis)}`);
    }
    if (check.level === 'query') reasons.push(`${l.product_name || li.description || 'A line'}: ${queryQuantityText({ billQty, poQty, kind, basis, conversion })}`);
    else if (check.level === 'note') notes.push(`${l.product_name || li.description || 'A line'}: a little different from the ${basis === 'supplied' ? 'supplied' : 'PO'} quantity.`);
  }

  // ── A cost that differs from the order's or the contract's: chosen in
  // review, never accepted from here (Cam, 2026-10-28; utils/billCostCheck) ──
  if (!credit) {
    for (const [k, g] of groups) {
      const po = poById[g.poId];
      if (!po || po.po_type === 'haulage') continue;
      const items = Array.isArray(po.line_items) ? po.line_items : [];
      const li = items.find((x, i) => poLineKey(x, i) === g.key);
      if (!li || li.kind === 'haulage') continue;
      const pctx = ctx.poContext?.[g.poId];
      const facts = pctx?.lines?.[g.key]?.cost_check || null;
      const bill = billCostInPoUnit({
        lines: g.lines, li, supplied: pctx?.noSupplied ? null : (pctx?.lines?.[g.key]?.supplied ?? null), itemWeight: unitWeightOf(li.itemCode),
      });
      const check = bill ? costCheck({ bill: bill.cost, po: li.unitAmount, order: facts?.order_cost ?? null, contract: facts?.contract || null }) : null;
      if (!check) continue;
      const kg = num(li.unitWeightKg) ?? unitWeightOf(li.itemCode);
      reasons.push(`${li.description || k}: ${costCheckText(check, { kg, poNumber: po.purchase_order_number, orderNumber: facts?.order_number || null, deliveryDate: facts?.delivery_date || null })} — choose its cost in review, or query the bill.`);
    }
  }

  if (item.parsed?.confidence === 'low') reasons.push('The reader was unsure of this one — check every figure.');
  if (item.parsed?.notes) notes.push(item.parsed.notes);
  if (item.last_error?.message) reasons.push(`Last save: ${item.last_error.message}`);

  // ── What it changes on its POs (Add Bill's default: every line ticked) ──
  const costChanges = credit ? [] : poCostChanges({
    lines: payloadLines.filter(l => linkedPoIds.includes(Number(l.purchase_order_id))),
    poById,
    poEdits: {},
    scopes: Object.fromEntries(Object.entries(ctx.poContext || {}).map(([id, c]) => [id, c?.scope || null])),
    xeroMade: Object.fromEntries(Object.entries(ctx.poContext || {}).map(([id, c]) => [id, !!c?.xeroMade])),
    noSupplied: Object.fromEntries(Object.entries(ctx.poContext || {}).map(([id, c]) => [id, !!c?.noSupplied])),
    orderLines: ctx.seeCosts ? Object.fromEntries(Object.entries(ctx.poContext || {}).map(([id, c]) => [id, c?.lines || {}])) : null,
    itemWeightOf: unitWeightOf,
    supplied: suppliedFromContext(ctx.poContext),
  });
  const ticks = defaultCostTicks(costChanges, { queried: false });

  // ── Duplicates: already in the system, or twice in this batch ──
  const dups = Array.isArray(item.duplicates) ? item.duplicates : [];
  const batchDup = item.batch_duplicate_of || null;
  const isDuplicate = dups.length > 0 || !!batchDup;

  const effects = [];
  if (linkedPoIds.length) effects.push(`marks ${linkedPoIds.map(id => poById[id]?.purchase_order_number || `PO #${id}`).join(', ')} billed`);
  const poMoves = costChanges.filter(c => c.po_cost_moves).length;
  const olOnly = costChanges.length - poMoves;
  if (poMoves) effects.push(`${plural(poMoves, 'PO cost')} differ${poMoves === 1 ? 's' : ''} from the bill — you choose which to update`);
  if (olOnly) effects.push(`${plural(olOnly, 'order line cost')} to choose about`);
  if (costChanges.some(c => c.xero_made && c.po_cost_moves)) effects.push('a PO made in Xero takes a chosen cost in Xero too');
  effects.push('goes to Xero as Approved');

  let status;
  if (isDuplicate && !item.dup_choice) status = 'duplicate';
  else if (item.dup_choice === 'replace') status = 'review';
  else if (reasons.length) status = 'review';
  else status = 'ready';
  if (item.dup_choice === 'replace') reasons.unshift('Replaces an existing bill — reviewed in Edit Bill.');

  const payload = status === 'ready' && supplier?.accountId ? {
    account_id: parseInt(supplier.accountId, 10),
    xero_contact_id: supplier.xeroContactId || null,
    supplier_name: supplier.name,
    purchase_order_ids: linkedPoIds,
    bill_date: moved.date,
    due_date: dueDate,
    reference: null,
    supplier_reference: supplierReference,
    status: 'Authorised',
    transaction_type: 'BILL',
    is_credit: false,
    is_queried: false,
    notes: null,
    sub_total: subTotal,
    total_tax: totalTax,
    total,
    line_items: payloadLines,
    po_edits: [],
    supplied_copies: [],
    confirmations: item.dup_choice === 'continue' ? { duplicate: true } : {},
    defer_push: false,
    // No PO cost choice here: Accept asks (acceptPayload).
    bulk_item_id: item.id,
    bulk_accept: true,
  } : null;

  return {
    ...plan,
    status,
    reasons,
    notes,
    effects,
    payload,
    costChanges,
    defaultTicks: ticks,
    supplier: supplier && supplier.accountId ? supplier : null,
    supplierReference,
    billDate: moved.date,
    totals: { subTotal, totalTax, total },
    invoiceTotals: { net: invoiceNet, vat: num(s.total_tax), total: invoiceTotal },
    linkedPoIds,
    poNumbers: linkedPoIds.map(id => poById[id]?.purchase_order_number || `PO #${id}`),
    duplicates: dups,
    batchDuplicateOf: batchDup,
  };
}

/** The progress across the top, from the server's counts. */
export function progressText(p) {
  if (!p || !p.count) return 'No bills yet';
  const bits = [`${plural(p.count, 'bill')}`, `${p.saved + (p.replaced || 0)} saved`];
  if (p.skipped) bits.push(`${p.skipped} skipped`);
  bits.push(`${p.todo} to do`);
  return bits.join(' · ');
}

/** "pages 2–3 of scan.pdf" / "scan.pdf". */
export function sourceText(item) {
  if (!item) return '';
  const whole = item.page_count === 1 || (item.page_from === 1 && item.page_to === item.page_count);
  if (whole) return item.source_name || '';
  const pages = item.page_from === item.page_to ? `page ${item.page_from}` : `pages ${item.page_from}–${item.page_to}`;
  return `${item.source_name || ''}, ${pages}`;
}

/** The order the review step walks: the rows chosen for review, by their place in the batch. */
export function reviewQueue(items) {
  return (items || [])
    .filter(i => !i.outcome && i.choice === 'review')
    .sort((a, b) => a.seq - b.seq);
}

/**
 * Can an existing bill be replaced from a document — i.e. edited through the
 * normal Edit Bill path? Null when it can; else the reason (voided, paid, in
 * a locked period, in Xero with editing there switched off, a credit…). The
 * server applies the same rules again on the save.
 *
 * @param {object} bill — a duplicate row (status, source, xero_bill_id, amount_paid, currency_code, transaction_type, is_credit, bill_date)
 * @param {{ switchOn?: boolean, canEditPosted?: boolean, firstAllowedDate?: string|null }} opts
 */
export function replaceBlock(bill, { switchOn = false, canEditPosted = false, firstAllowedDate = null } = {}) {
  if (!bill) return 'No bill to replace';
  const mode = billEditMode(bill, { switchOn, canEditPosted });
  if (!mode.editable) return mode.block || 'This bill cannot be edited here';
  if (mode.mode === 'local' && Math.abs(parseFloat(bill.amount_paid || 0)) > 0.005) {
    return 'It has payments against it — remove them in Xero first';
  }
  const day = String(bill.bill_date || '').slice(0, 10);
  if (firstAllowedDate && /^\d{4}-\d{2}-\d{2}$/.test(day) && day < firstAllowedDate) {
    return 'Its date is in a period locked in Xero';
  }
  return null;
}

/**
 * Accept's save for one bill: the plan's, with the PO cost choice the user
 * made in the Accept window — every PO line that differs, ticked or not
 * (utils/billCostWriteBack.poCostUpdatesPayload, as Add Bill sends it).
 * Null when the plan cannot be accepted.
 */
export function acceptPayload(plan, ticks = {}) {
  if (!plan || plan.status !== 'ready' || !plan.payload) return null;
  return { ...plan.payload, po_cost_updates: poCostUpdatesPayload(plan.costChanges || [], ticks || {}) };
}
